import { useEffect, useMemo } from 'react';
import { REACT_APP_SENDBIRD_ID } from '../constants/envConstants';
import {
  isLegacySendbirdChannel,
  isSendbirdSessionExpired,
  toConnectionCredentials,
} from '../helpers/sendbird';
import { useAppDispatch, useAppSelector } from './storeHooks';
import {
  fetchLegacyVendorOwnerSendbirdSessionThunk,
  fetchTeamMemberSendbirdSessionThunk,
} from '../store/user/userThunk';

const EXPIRY_BUFFER_MS = 60_000;

/**
 * NAZ-604: Ensures the correct Sendbird session for channel mode and returns UIKit credentials.
 * @param isNewModelChannel from API (`isNewModelChannel` / `isSupportSendbirdNewModel`)
 * @param vendorSendBirdId vendor owner Sendbird id for legacy channels
 */
export function useSendbirdConnection(
  isNewModelChannel?: boolean,
  vendorSendBirdId?: string | null,
) {
  const dispatch = useAppDispatch();
  const legacy = isLegacySendbirdChannel(isNewModelChannel);
  const {
    sendbirdApplicationId,
    teamMemberSendbirdSession,
    legacyVendorOwnerSendbirdSession,
    isTeamMemberSendbirdTokenLoading,
    isLegacySendbirdTokenLoading,
    teamMemberSendbirdApiError,
    legacySendbirdApiError,
    userDetails,
  } = useAppSelector((state) => state.user);

  const isTokenLoading = legacy ? isLegacySendbirdTokenLoading : isTeamMemberSendbirdTokenLoading;
  const connectionError = legacy ? legacySendbirdApiError : teamMemberSendbirdApiError;

  const teamMemberSendbirdId = userDetails?.sendbirdId ?? '';
  const legacyOwnerId = vendorSendBirdId ?? '';

  useEffect(() => {
    if (legacy) {
      if (!legacyOwnerId) return;
      const session = legacyVendorOwnerSendbirdSession;
      if (
        !session ||
        session.sendbirdId !== legacyOwnerId ||
        isSendbirdSessionExpired(session.expiresAt)
      ) {
        void dispatch(fetchLegacyVendorOwnerSendbirdSessionThunk({ sendbirdUserId: legacyOwnerId }));
      }
      return;
    }
    if (!teamMemberSendbirdSession || isSendbirdSessionExpired(teamMemberSendbirdSession.expiresAt)) {
      void dispatch(fetchTeamMemberSendbirdSessionThunk());
    }
  }, [
    dispatch,
    legacy,
    legacyOwnerId,
    teamMemberSendbirdSession,
    legacyVendorOwnerSendbirdSession,
  ]);

  const session = useMemo(() => {
    if (legacy) {
      if (
        legacyVendorOwnerSendbirdSession?.sendbirdId === legacyOwnerId &&
        !isSendbirdSessionExpired(legacyVendorOwnerSendbirdSession.expiresAt)
      ) {
        return legacyVendorOwnerSendbirdSession;
      }
      return null;
    }
    if (teamMemberSendbirdSession && !isSendbirdSessionExpired(teamMemberSendbirdSession.expiresAt)) {
      return teamMemberSendbirdSession;
    }
    return null;
  }, [legacy, legacyOwnerId, legacyVendorOwnerSendbirdSession, teamMemberSendbirdSession]);

  useEffect(() => {
    if (!session?.expiresAt || isSendbirdSessionExpired(session.expiresAt)) return;

    const refreshAt = session.expiresAt - EXPIRY_BUFFER_MS;
    const delayMs = Math.max(0, refreshAt - Date.now());
    const timer = window.setTimeout(() => {
      if (legacy) {
        if (!legacyOwnerId) return;
        void dispatch(fetchLegacyVendorOwnerSendbirdSessionThunk({ sendbirdUserId: legacyOwnerId }));
        return;
      }
      void dispatch(fetchTeamMemberSendbirdSessionThunk({ force: true }));
    }, delayMs);

    return () => window.clearTimeout(timer);
  }, [dispatch, legacy, legacyOwnerId, session?.expiresAt, session?.sendbirdId]);

  const credentials = useMemo(() => {
    if (!session) return null;
    return toConnectionCredentials(session);
  }, [session]);

  const applicationId = sendbirdApplicationId || credentials?.applicationId || REACT_APP_SENDBIRD_ID;

  return {
    isLegacyChannel: legacy,
    applicationId,
    userId: credentials?.userId ?? '',
    accessToken: credentials?.accessToken ?? '',
    isReady: !!credentials?.accessToken && !isTokenLoading,
    isLoading: isTokenLoading,
    error: connectionError,
    teamMemberSendbirdId,
  };
}
